'use client';

import { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import { RenderHTML } from '@/components/renderHtml';
import { StyledTextProps } from '@/types/styledText';
import { usePageTheme } from '@/stores/page-theme';
import { getThemeColor, ThemeColor } from '@/theme/themeColors';
import { backgrounds, BackgroundValue } from '@/theme/backgrounds';
import Heading from '@/components/common/Heading';

export const StyledText: FC<StyledTextProps> = ({
  content,
  textStyle,
  textColor,
  startColor,
  middleColor,
  endColor,
  alignment,
  size,
  theme,
  background,
  backgroundColor,
  backgroundImage,
  bgColor: customBgColor,
}) => {
  const { bgColor: themeBgColor } = usePageTheme();

  const getTextStyle = () => {
    if (textStyle === 'solid' && textColor) {
      return { color: textColor };
    }
    if (textStyle === 'gradient') {
      return {
        background: `linear-gradient(to right, ${startColor}, ${middleColor}, ${endColor})`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      };
    }
    if (theme) {
      const themeColor = getThemeColor(theme as ThemeColor);
      switch (themeColor) {
        case 'purple':
        case 'black':
          return { color: backgrounds.white };
        default:
          return { color: '#2B2A2B' };
      }
    }
    return {};
  };

  const getAlignmentClass = () => {
    switch (alignment) {
      case 'left':
        return 'text-left';
      case 'right':
        return 'text-right';
      case 'center':
        return 'text-center';
    }
  };

  const getBackgroundClass = () => {
    if (background === 'inherit' && !backgroundColor) {
      if (customBgColor) return customBgColor;
      if (theme && theme !== 'default') {
        const themeColor = getThemeColor(theme as ThemeColor);
        switch (themeColor) {
          case 'purple':
            return `bg-[${backgrounds.purple}]`;
          case 'black':
            return 'bg-black';
          default:
            return themeBgColor;
        }
      }
      return '';
    }
    return '';
  };

  const getBackgroundStyle = () => {
    if (background === 'solid' && backgroundColor) {
      return { backgroundColor };
    }

    if (background) {
      switch (background) {
        case 'gradient':
          return { background: backgrounds.purpleGradientCombined };
        case 'gradient-mesh':
          return { background: backgrounds.purpleBluePinkMesh };
        case 'image':
          return backgroundImage
            ? { backgroundImage: `url(${backgroundImage})` }
            : {};
        default:
          return {};
      }
    }

    if (backgroundImage) {
      return { backgroundImage: `url(${backgroundImage})` };
    }
    return {};
  };

  const getHeadingSize = () => {
    if (size === '40PX') return '40px';
    return parseInt(size.substring(1));
  };

  const textStyleObj = getTextStyle();
  const alignmentClass = getAlignmentClass();
  const backgroundClass = getBackgroundClass();
  const backgroundStyle = getBackgroundStyle();
  const headingSize = getHeadingSize();

  return (
    <div
      className={`relative w-full ${backgroundClass}`}
      style={backgroundStyle}
    >
      <div className='container mx-auto px-5 py-14 md:px-16 md:py-20 xl:px-[7.5rem] xl:py-24'>
        <div className={`mx-auto max-w-3xl ${alignmentClass}`}>
          <Heading
            size={headingSize}
            className={twMerge('font-bold')}
            style={textStyleObj}
          >
            <RenderHTML data={content} />
          </Heading>
        </div>
      </div>
    </div>
  );
};
